import { atom, type Setter } from "jotai";

import type { CaptionResult } from "./types";

/**
 * The CURRENTLY displayed caption text for the program. A single global
 * atom (the audio is the full program mix, so there is exactly one ASR
 * stream regardless of how many caption widgets are on the canvas).
 *
 * The string holds the rendered block, which may contain newlines when
 * `maxLines > 1` — committed lines stacked on top of the live (interim)
 * line, e.g.:
 *
 *     ...previous committed line
 *     the line currently being spoken
 *
 * `LiveCaptionsWidget` subscribes to this and paints it; the config /
 * service write to it via {@link applyCaptionResult}.
 */
export const captionTextAtom = atom<string>("");

/**
 * Internal rolling state behind {@link captionTextAtom}: the list of
 * already-committed (final) lines and the current interim line. Kept in a
 * module-local mutable ref rather than a second atom so callers only need
 * `set` — the helper recomputes `captionTextAtom`'s string from it.
 *
 * Reset whenever the ASR session restarts (see {@link resetCaptionState}).
 */
interface CaptionRollingState {
  /** Committed (final) lines, oldest → newest, clamped to `maxLines`. */
  committed: string[];
  /** The live, not-yet-final line (interim). Empty string when none. */
  interim: string;
}

const rollingState: CaptionRollingState = {
  committed: [],
  interim: "",
};

/**
 * Compose the visible block from committed lines + the interim line,
 * clamped to the most recent `keep` lines total so the widget never shows
 * more than `maxLines`.
 */
const composeBlock = (state: CaptionRollingState, keep: number): string => {
  const lines = state.interim
    ? [...state.committed, state.interim]
    : [...state.committed];
  const clamped =
    lines.length > keep ? lines.slice(lines.length - keep) : lines;
  return clamped.join("\n");
};

/**
 * Apply a {@link CaptionResult} to {@link captionTextAtom}.
 *
 * Behaviour (per the fixed contract):
 * - An interim result (`isFinal === false`) REPLACES the live line.
 * - A final result (`isFinal === true`) COMMITS the line and clears the
 *   interim. When `maxLines > 1` the most recent `maxLines - 1` committed
 *   lines are retained above the (next) live line, so the visible block
 *   never exceeds `maxLines`. When `maxLines <= 1` only the single most
 *   recent line is shown.
 *
 * Empty-text results are ignored (the sidecar occasionally emits empty
 * interim segments between utterances).
 *
 * @param set      Jotai setter (from a write atom, `useSetAtom`, or
 *                 `store.set`).
 * @param result   The parsed caption line from the sidecar.
 * @param maxLines How many lines the widget renders (>= 1).
 */
export const applyCaptionResult = (
  set: Setter,
  result: CaptionResult,
  maxLines: number,
): void => {
  const text = (result.text ?? "").trim();
  const keep = Math.max(1, Math.floor(maxLines || 1));

  if (!result.isFinal) {
    // Interim → replace the live line. Skip pure-empty interims so we
    // don't blank the screen between words.
    if (!text && !rollingState.interim) return;
    rollingState.interim = text;
    set(captionTextAtom, composeBlock(rollingState, keep));
    return;
  }

  // Final → commit. Ignore empty finals (nothing to commit).
  if (!text) {
    // Still clear a stale interim so the live line doesn't linger.
    if (rollingState.interim) {
      rollingState.interim = "";
      set(captionTextAtom, composeBlock(rollingState, keep));
    }
    return;
  }

  rollingState.committed.push(text);
  rollingState.interim = "";

  // Cap the committed history at `keep` lines. `composeBlock` does the
  // final visible-line clamp (committed + interim ≤ keep), but we trim the
  // backing array too so it can't grow unbounded across a long session.
  if (rollingState.committed.length > keep) {
    rollingState.committed.splice(0, rollingState.committed.length - keep);
  }

  set(captionTextAtom, composeBlock(rollingState, keep));
};

/**
 * Clears the rolling caption state and the displayed text. Call this when
 * the ASR session starts or stops so a new session doesn't inherit the
 * previous session's tail.
 */
export const resetCaptionState = (set: Setter): void => {
  rollingState.committed = [];
  rollingState.interim = "";
  set(captionTextAtom, "");
};
